import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService, ConsultantRequestFilters, ConsultantRequestStatus } from '../services/api';
import toast from 'react-hot-toast';

export const useConsultantRequests = (params?: ConsultantRequestFilters) => {
  return useQuery({
    queryKey: ['consultantRequests', params],
    queryFn: () => apiService.getConsultantRequests(params),
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1,
    retryDelay: 1000,
  });
};

export const useConsultantRequestStats = () => {
  return useQuery({
    queryKey: ['consultantRequestStats'],
    queryFn: () => apiService.getConsultantRequestStats(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    retryDelay: 1000,
  });
};

export const useUpdateConsultantRequestStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, status, adminNotes }: { requestId: string; status: ConsultantRequestStatus; adminNotes?: string }) =>
      apiService.updateConsultantRequestStatus(requestId, status, adminNotes),
    onSuccess: () => {
      toast.success('Status updated successfully');
      queryClient.invalidateQueries({ queryKey: ['consultantRequests'] });
      queryClient.invalidateQueries({ queryKey: ['consultantRequestStats'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update status');
    },
  });
};

export const useConvertConsultantRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, contractData }: { requestId: string; contractData: { energyContractNumber: string; energyContractPartner: string } }) =>
      apiService.convertConsultantRequest(requestId, contractData),
    onSuccess: () => {
      toast.success('Request converted successfully! Customer contract updated.');
      queryClient.invalidateQueries({ queryKey: ['consultantRequests'] });
      queryClient.invalidateQueries({ queryKey: ['consultantRequestStats'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to convert request');
    },
  });
};
